import React, { useEffect, useState } from 'react';

interface Conv {
    id: number;
    channel: string;
    contact_handle: string;
    contact_name: string | null;
    last_msg_at: string;
    last_msg_preview: string | null;
    status: string;
    message_count: number;
}

interface Message {
    id: number;
    direction: 'in' | 'out';
    sent_at: string;
    body_text: string;
    sent_by: string;
    delivery_status: string;
}

export const InboxPage: React.FC = () => {
    const [convs, setConvs] = useState<Conv[]>([]);
    const [activeId, setActiveId] = useState<number | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/admin/inbox/conversations', { credentials: 'include' })
            .then(r => r.json())
            .then(d => setConvs(d.conversations || []))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!activeId) return;
        fetch(`/api/admin/inbox/conversations/${activeId}`, { credentials: 'include' })
            .then(r => r.json())
            .then(d => setMessages(d.messages || []));
    }, [activeId]);

    const send = async () => {
        if (!activeId || !reply.trim() || sending) return;
        setSending(true);
        try {
            const r = await fetch(`/api/admin/inbox/conversations/${activeId}/reply`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ body_text: reply }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.error || 'send_failed');
            setReply('');
            // refresh thread
            const t = await fetch(`/api/admin/inbox/conversations/${activeId}`, { credentials: 'include' }).then(r => r.json());
            setMessages(t.messages || []);
        } catch (e) {
            alert(`Błąd wysyłki: ${e}`);
        } finally {
            setSending(false);
        }
    };

    const active = convs.find(c => c.id === activeId);

    return (
        <div>
            <h1 className="text-2xl font-bold mb-4">Inbox (rozmowy)</h1>
            <div className="flex gap-4 h-[calc(100vh-180px)]">
                <div className="w-80 bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark overflow-y-auto">
                    {loading && <div className="p-4 text-sm">Ładowanie…</div>}
                    {!loading && convs.length === 0 && (
                        <div className="p-4 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Brak rozmów. Pojawią się tu inbound z WhatsApp / Messenger / IG DM.
                        </div>
                    )}
                    {convs.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setActiveId(c.id)}
                            className={`w-full text-left p-3 border-b border-border-light dark:border-border-dark hover:bg-bkg-light dark:hover:bg-bkg-dark ${
                                c.id === activeId ? 'bg-primary-light/10 dark:bg-primary-dark/10' : ''
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-semibold text-sm">{c.contact_name || c.contact_handle}</span>
                                <span className="text-[10px] uppercase text-text-secondary-light/70 dark:text-text-secondary-dark/70">
                                    {c.channel}
                                </span>
                            </div>
                            <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                                {c.last_msg_preview || '(brak)'}
                            </div>
                            <div className="text-[10px] text-text-secondary-light/60 dark:text-text-secondary-dark/60">
                                {c.last_msg_at} · {c.message_count} msg
                            </div>
                        </button>
                    ))}
                </div>

                <div className="flex-1 bg-white dark:bg-content-dark rounded-xl border border-border-light dark:border-border-dark flex flex-col">
                    {!active ? (
                        <div className="flex-1 flex items-center justify-center text-text-secondary-light dark:text-text-secondary-dark text-sm">
                            Wybierz rozmowę z listy
                        </div>
                    ) : (
                        <>
                            <div className="p-3 border-b border-border-light dark:border-border-dark">
                                <div className="font-semibold">{active.contact_name || active.contact_handle}</div>
                                <div className="text-xs text-text-secondary-light dark:text-text-secondary-dark">{active.channel} · {active.status}</div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                {messages.map(m => (
                                    <div
                                        key={m.id}
                                        className={`max-w-[70%] p-2 rounded-lg text-sm ${
                                            m.direction === 'in'
                                                ? 'bg-bkg-light dark:bg-bkg-dark mr-auto'
                                                : 'bg-primary-light dark:bg-primary-dark text-white ml-auto'
                                        }`}
                                    >
                                        <div>{m.body_text}</div>
                                        <div className="text-[10px] mt-1 opacity-70">
                                            {m.sent_at} · {m.sent_by} · {m.delivery_status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-3 border-t border-border-light dark:border-border-dark flex gap-2">
                                <input
                                    type="text"
                                    value={reply}
                                    onChange={e => setReply(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && send()}
                                    placeholder="Twoja odpowiedź…"
                                    className="flex-1 px-3 py-2 border rounded-md bg-bkg-light dark:bg-bkg-dark"
                                    disabled={sending || active.channel !== 'wa_cloud'}
                                />
                                <button
                                    onClick={send}
                                    disabled={sending || !reply.trim() || active.channel !== 'wa_cloud'}
                                    className="px-4 py-2 bg-primary-light dark:bg-primary-dark text-white rounded-md font-semibold disabled:opacity-50"
                                >
                                    {sending ? 'Wysyłam…' : 'Wyślij'}
                                </button>
                            </div>
                            {active.channel !== 'wa_cloud' && (
                                <div className="px-3 pb-3 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                    ℹ️ Wysyłka dostępna tylko dla wa_cloud (Phase 1.3). Messenger/IG DM — Phase 2.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
